import { ChevronLeft, ChevronRight, Home } from 'lucide-react';
import { useEffect, useState } from 'react';
import type { LLMTrace } from '../../ai-tracer-integration/types/ai-tracer-types';
import {
	TabbedDetailModal,
	TraceTabContent,
} from '../components/tabbed-detail-modal';
import type { TraceRow } from './trace-table-config';

interface TraceDetailModalProps {
	trace: TraceRow | null;
	traceFamily?: {
		rootTrace: TraceRow;
		selectedTraceId: string;
	} | null;
	onClose: () => void;
}

export const TraceDetailModal = ({
	trace,
	traceFamily,
	onClose,
}: TraceDetailModalProps) => {
	const [currentTrace, setCurrentTrace] = useState<TraceRow | null>(trace);

	// Reset current trace when the initial trace prop changes, or set from traceFamily selection
	useEffect(() => {
		if (traceFamily) {
			// Find the initially selected trace in the trace family
			const initialTrace = findTraceInHierarchy(
				traceFamily.rootTrace,
				traceFamily.selectedTraceId,
			);
			setCurrentTrace(initialTrace || traceFamily.rootTrace);
		} else {
			setCurrentTrace(trace);
		}
	}, [trace, traceFamily]);

	if (!trace) {
		return null;
	}

	// Helper to find a specific trace in the hierarchy by ID
	const findTraceInHierarchy = (
		rootTrace: TraceRow,
		targetId: string,
	): TraceRow | null => {
		if (rootTrace.id === targetId) return rootTrace;

		if (rootTrace.children && rootTrace.children.length > 0) {
			for (const child of rootTrace.children) {
				const found = findTraceInHierarchy(child, targetId);
				if (found) return found;
			}
		}
		return null;
	};

	// Get all traces in the hierarchy (flattened)
	const getAllTracesInHierarchy = (rootTrace: TraceRow): TraceRow[] => {
		const traces: TraceRow[] = [rootTrace];

		const addChildren = (trace: TraceRow) => {
			if (trace.children && trace.children.length > 0) {
				trace.children.forEach((child: LLMTrace) => {
					traces.push(child);
					addChildren(child);
				});
			}
		};

		addChildren(rootTrace);
		return traces;
	};

	// Use traceFamily if available, otherwise fallback to original logic
	const effectiveRootTrace = traceFamily?.rootTrace || trace;
	const allTraces = getAllTracesInHierarchy(effectiveRootTrace);
	const currentIndex = allTraces.findIndex(
		(t) => t.id === (currentTrace?.id || trace.id),
	);
	const isAtRoot = (currentTrace?.id || trace.id) === effectiveRootTrace.id;

	// Navigation functions
	const goToRoot = () => setCurrentTrace(effectiveRootTrace);
	const goToPrevious = () => {
		if (currentIndex > 0) {
			setCurrentTrace(allTraces[currentIndex - 1]);
		}
	};
	const goToNext = () => {
		if (currentIndex < allTraces.length - 1) {
			setCurrentTrace(allTraces[currentIndex + 1]);
		}
	};

	// Get navigation info
	const hasChildren = (currentTrace?.children?.length || 0) > 0;
	const childrenCount = currentTrace?.children?.length || 0;
	const canGoBack = currentIndex > 0;
	const canGoForward = currentIndex < allTraces.length - 1;
	const currentPosition = currentIndex + 1;
	const totalTraces = allTraces.length;

	// Determine what the "Home" button should do and show
	const getHomeInfo = () => {
		if (allTraces.length === 1) {
			// Single trace, no navigation needed
			return {
				canGoHome: false,
				homeLabel: 'Only Trace',
				homeTitle: 'This is the only trace',
			};
		}

		if (isAtRoot) {
			// Currently at root/parent
			return {
				canGoHome: false,
				homeLabel: 'At Root',
				homeTitle: 'Already at parent trace',
			};
		}
		// Currently at child, can go back to parent
		return {
			canGoHome: true,
			homeLabel: 'To Parent',
			homeTitle: 'Go back to parent trace',
		};
	};

	const homeInfo = getHomeInfo();

	// Create navigation header component
	const NavigationHeader = () => (
		<div className='tw-flex tw-items-center tw-justify-between tw-px-6 tw-py-3 tw-bg-gray-50 tw-border-b tw-border-gray-200'>
			<div className='tw-flex tw-items-center tw-gap-3 tw-text-sm'>
				<button
					type='button'
					onClick={goToRoot}
					disabled={!homeInfo.canGoHome}
					className={`tw-flex tw-items-center tw-gap-1 tw-px-2 tw-py-1 tw-rounded tw-transition-colors ${
						!homeInfo.canGoHome
							? 'tw-text-gray-400 tw-cursor-not-allowed'
							: 'tw-text-blue-600 hover:tw-text-blue-800 hover:tw-bg-blue-50'
					}`}
					title={homeInfo.homeTitle}
				>
					<Home className='tw-w-4 tw-h-4' />
					<span className='tw-font-medium'>{homeInfo.homeLabel}</span>
				</button>
				<span className='tw-text-gray-300'>|</span>
				<span className='tw-font-medium tw-text-gray-600'>
					Current: {currentTrace?.function_id || 'Unknown'}
					{currentTrace?.parent_span_id && !isAtRoot && (
						<span className='tw-text-xs tw-text-gray-500 tw-ml-1'>(child)</span>
					)}
				</span>
				{hasChildren && (
					<span className='tw-text-gray-500'>
						({childrenCount} child{childrenCount !== 1 ? 'ren' : ''})
					</span>
				)}
			</div>
			<div className='tw-flex tw-items-center tw-gap-2 tw-text-sm'>
				<span className='tw-text-gray-500'>
					{currentPosition} of {totalTraces}
				</span>
				<div className='tw-flex tw-items-center tw-gap-1'>
					<button
						type='button'
						onClick={goToPrevious}
						disabled={!canGoBack}
						className={`tw-p-1 tw-rounded tw-transition-colors ${
							canGoBack
								? 'tw-text-gray-600 hover:tw-text-gray-800 hover:tw-bg-gray-200'
								: 'tw-text-gray-300 tw-cursor-not-allowed'
						}`}
						title='Previous trace'
					>
						<ChevronLeft className='tw-w-4 tw-h-4' />
					</button>
					<button
						type='button'
						onClick={goToNext}
						disabled={!canGoForward}
						className={`tw-p-1 tw-rounded tw-transition-colors ${
							canGoForward
								? 'tw-text-gray-600 hover:tw-text-gray-800 hover:tw-bg-gray-200'
								: 'tw-text-gray-300 tw-cursor-not-allowed'
						}`}
						title='Next trace'
					>
						<ChevronRight className='tw-w-4 tw-h-4' />
					</button>
				</div>
			</div>
		</div>
	);

	return (
		<TabbedDetailModal
			isOpen={!!trace}
			onClose={onClose}
			title='Trace Details'
			navigationHeader={<NavigationHeader />}
			tabs={[
				{
					id: 'trace',
					label: 'Trace',
					content: (
						<TraceTabContent
							trace={currentTrace || trace}
							homeInfo={homeInfo}
						/>
					),
				},
			]}
		/>
	);
};
